/**
 * Debug Service
 *
 * Captures current page state by calling renderMarkdown() on page components.
 * Each page is responsible for generating its own markdown representation.
 */

import { format } from "date-fns";
import { authService } from "../auth";
import { apiBase } from "../api-config";

/**
 * Interface for components that can render their state as markdown.
 * Pages that support debug capture should implement this interface.
 */
export interface DebugRenderable {
  renderMarkdown(): string;
}

/**
 * Type guard to check if an element implements DebugRenderable
 */
export function isDebugRenderable(el: unknown): el is DebugRenderable {
  return (
    typeof el === "object" &&
    el !== null &&
    "renderMarkdown" in el &&
    typeof (el as DebugRenderable).renderMarkdown === "function"
  );
}

export interface DebugPageState {
  pageName: string;
  url: string;
  timestamp: string;
  pageMarkdown: string;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
}

export interface DebugReport {
  timestamp: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  pageName: string;
  userNote: string;
  markdown: string;
  rawState: DebugPageState;
}

// Admin API types
export interface DebugReportSummary {
  reportId: string;
  pageName: string;
  userNote: string;
  userDisplayName: string;
  userEmail: string;
  createdAt: string;
  isResolved: boolean;
  resolvedAt?: string;
}

export interface DebugReportDetail {
  reportId: string;
  pageName: string;
  userNote: string;
  markdown: string;
  userDisplayName: string;
  userEmail: string;
  userId: string;
  url: string;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
  createdAt: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolutionNote?: string;
}

class DebugService {
  /**
   * Capture the current page state by finding the page component and calling renderMarkdown()
   */
  capturePageState(): DebugPageState {
    const pageName = this.getPageTitle();
    const pageMarkdown = this.capturePageMarkdown();

    return {
      pageName,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      pageMarkdown,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Get the page title from app-shell or document
   */
  private getPageTitle(): string {
    const appShell = document.querySelector("app-shell");
    if (appShell) {
      const title = (appShell as any).pageTitle;
      if (title) return title;
    }
    return document.title || window.location.pathname;
  }

  /**
   * Find the current page component and call its renderMarkdown() method
   */
  private capturePageMarkdown(): string {
    // Find the page element slotted into app-shell
    const appShell = document.querySelector("app-shell");
    let pageElement: Element | null = null;

    if (appShell?.shadowRoot) {
      const slot = appShell.shadowRoot.querySelector("slot");
      const slottedElements = slot?.assignedElements() || [];
      pageElement = slottedElements[0] || null;
    }

    if (!pageElement) {
      // Fallback: look for known page elements
      pageElement =
        document.querySelector("home-page") ||
        document.querySelector("add-entry-page") ||
        document.querySelector("history-page");
    }

    // Check if the page implements DebugRenderable
    if (pageElement && isDebugRenderable(pageElement)) {
      try {
        return pageElement.renderMarkdown();
      } catch (err) {
        return `(Error capturing page state: ${err instanceof Error ? err.message : String(err)})`;
      }
    }

    // Page doesn't implement renderMarkdown
    const tagName = pageElement?.tagName?.toLowerCase() || "unknown";
    return `(Page "${tagName}" does not implement renderMarkdown())`;
  }

  /**
   * Generate the full markdown report
   */
  generateMarkdown(state: DebugPageState): string {
    const lines: string[] = [];

    lines.push(`# Debug Report: ${state.pageName}`);
    lines.push("");
    lines.push(
      `**Captured:** ${format(new Date(state.timestamp), "yyyy-MM-dd HH:mm:ss")}`,
    );
    lines.push(`**URL:** ${state.url}`);
    lines.push(`**Screen:** ${state.screenWidth} x ${state.screenHeight}`);
    lines.push("");
    lines.push("---");
    lines.push("");
    lines.push("## Page Content");
    lines.push("");
    lines.push(state.pageMarkdown || "(No content captured)");
    lines.push("");
    lines.push("---");
    lines.push("");
    lines.push("## Device Info");
    lines.push("");
    lines.push(`**User Agent:** ${state.userAgent}`);

    return lines.join("\n");
  }

  /**
   * Submit a debug report to the server
   */
  async submitReport(
    state: DebugPageState,
    userNote: string,
  ): Promise<{ success: boolean; error?: string }> {
    const auth = authService.state;

    if (!auth.isAuthenticated || !auth.userId) {
      return { success: false, error: "Not authenticated" };
    }

    const markdown = this.generateMarkdown(state);

    const report: DebugReport = {
      timestamp: new Date().toISOString(),
      userId: auth.userId,
      userEmail: auth.email || "",
      userDisplayName: auth.displayName || "",
      pageName: state.pageName,
      userNote,
      markdown,
      rawState: state,
    };

    try {
      const response = await fetch(`${apiBase}/api/debug/report`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(report),
      });

      if (!response.ok) {
        const text = await response.text();
        return { success: false, error: text || `HTTP ${response.status}` };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Network error",
      };
    }
  }

  /**
   * Get pages that support debug capture
   */
  getDebugEnabledPages(): string[] {
    return ["/", "/add", "/history"];
  }

  /**
   * Check if debug is enabled for current path
   */
  isDebugEnabled(path: string): boolean {
    return this.getDebugEnabledPages().includes(path);
  }

  // ========================================================================
  // Admin API methods
  // ========================================================================

  /**
   * Get all debug reports (admin only)
   */
  async getReports(
    includeResolved = false,
    limit = 100,
  ): Promise<DebugReportSummary[]> {
    const params = new URLSearchParams({
      includeResolved: String(includeResolved),
      limit: String(limit),
    });

    const response = await fetch(`${apiBase}/api/debug/reports?${params}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Admin access required");
      }
      throw new Error("Failed to load debug reports");
    }

    return response.json();
  }

  /**
   * Get a single debug report (admin only)
   */
  async getReport(reportId: string): Promise<DebugReportDetail> {
    const response = await fetch(`${apiBase}/api/debug/reports/${reportId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Admin access required");
      }
      if (response.status === 404) {
        throw new Error("Report not found");
      }
      throw new Error("Failed to load debug report");
    }

    return response.json();
  }

  /**
   * Delete a debug report (admin only)
   */
  async deleteReport(reportId: string): Promise<void> {
    const response = await fetch(`${apiBase}/api/debug/reports/${reportId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Admin access required");
      }
      if (response.status === 404) {
        throw new Error("Report not found");
      }
      throw new Error("Failed to delete debug report");
    }
  }

  /**
   * Mark a debug report as resolved (admin only)
   */
  async resolveReport(reportId: string, note?: string): Promise<void> {
    const response = await fetch(
      `${apiBase}/api/debug/reports/${reportId}/resolve`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ note }),
      },
    );

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error("Admin access required");
      }
      if (response.status === 404) {
        throw new Error("Report not found");
      }
      throw new Error("Failed to resolve debug report");
    }
  }
}

export const debugService = new DebugService();
